"use client"

import { useState } from 'react'
import { truncateToTwoDecimals } from './../../lib/util'

export default function OverallChargesModal({ setShowOverallChargesModal, overAllCharges, setOverAllCharges }) {

    const [errors, setErrors] = useState([])

    const handleSubmit = (e) => {
        e.preventDefault()

        // Validate if charge name and charge values are present in all rows
        const validationErrors = []

        overAllCharges.forEach((charge, index) => {
            if (!charge.name.trim()) {
                validationErrors.push(`Charge name is required for row ${index + 1}`)
            }
            if (!charge.value || charge.value <= 0) {
                validationErrors.push(`Charge value must be greater than 0 for row ${index + 1}`)
            }
        })

        setErrors(validationErrors)

        // If no error is present then save to invoice
        if (validationErrors.length === 0) {
            // Close modal after successful validation
            setShowOverallChargesModal(false)
        }
    }

    const handleChange = (index, e, overAllCharges) => {
      const newCharges = [...overAllCharges]
      const inputValue = e.target.name === 'value' ? truncateToTwoDecimals(e.target.valueAsNumber) : e.target.value
      newCharges[index] = {...newCharges[index], [e.target.name]: inputValue}
      setOverAllCharges(newCharges)

      // Clear errors when user starts typing
      if (errors.length > 0) {
        setErrors([])
      }
    }

    const addNewCharge = () => {
      setOverAllCharges([...overAllCharges, {
          name: '',
          placeholder: 'Transportation Fee, Discount, etc.',
          isDiscount: false,
          value: 0.00,
      }])
    }

    const removeCharge = (index) => {
      const newCharges = [...overAllCharges]
      newCharges.splice(index, 1)
      setOverAllCharges(newCharges)
    }

    const toggleDiscountVsCharge = (index) => {
      const newCharges = [...overAllCharges]
      const isDiscount = newCharges[index].isDiscount || false
      newCharges[index] = {...newCharges[index], isDiscount: !isDiscount}
      setOverAllCharges(newCharges)
    }

    return (
      <div className="fixed inset-0 backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-xl w-full shadow-2xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              Add Additional Charges / Discounts
            </h2>
            <button 
              onClick={() => setShowOverallChargesModal(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Display */}
          {errors.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Please fix the following errors:</h3>
                  <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={(e) => handleSubmit(e)}>
            <div className="space-y-4">
              {overAllCharges?.map((charge, index) => (
                  <div className="space-y-3" key={index}>
                      <div className="flex space-x-2 justify-between items-center">
                          <div className="w-1/2">
                              <label className="block text-sm font-medium text-gray-700 mb-2">Charge Name</label>
                              <input type="text" value={charge?.name || ''} name="name" placeholder={charge?.placeholder || ''} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" onChange={(e) => handleChange(index, e, overAllCharges)} required />
                          </div>
                          <div className="w-1/2">
                              <label className="block text-sm font-medium text-gray-700 mb-2">Charge Value</label>
                              <input type="number" name="value" value={charge?.value || ''} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter value" required onChange={(e) => handleChange(index, e, overAllCharges)}/>
                          </div>
                      </div>
                      <div className="flex items-center justify-between">
                          <div className="flex items-center font-semibold sm:font-normal">
                              <button
                                  onClick={() => toggleDiscountVsCharge(index)}
                                  type="button"
                                  className={`sm:px-2 sm:py-0.5 px-3 py-1 text-xs rounded-l-md ${charge.isDiscount ? 'bg-gray-200 text-gray-700' : 'bg-blue-600 text-white'}`}
                              >
                                  Charge
                              </button>
                              <button
                                  onClick={() => toggleDiscountVsCharge(index)}
                                  type="button"
                                  className={`relative group sm:px-2 flex sm:py-0.5 px-3 py-1 text-xs rounded-r-md ${charge.isDiscount ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                              >
                                  Discount
                              </button>
                          </div>
                      </div>
                  </div>
              ))}
            </div>
            <div className="mt-12 px-2 py-1 bg-gray-50 border-gray-200 rounded-lg text-sm">
                <button type="button" 
                  onClick={addNewCharge} 
                  className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 font-medium transition-colors cursor-pointer">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Add new entry</span>
                </button>
                {(overAllCharges.length > 1) && (
                  <button type="button" 
                    onClick={removeCharge} 
                    className="flex items-center space-x-2 text-red-500 hover:text-red-600 font-medium transition-colors cursor-pointer">
                      <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 14">
                        <path d="M3 8a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9A.5.5 0 0 1 3 8z" />
                      </svg>
                    <span>Remove entry</span>
                </button>
                )}
            </div>
            <div className="flex space-x-3 mt-8">
              <button 
                type="button"
                onClick={() => setShowOverallChargesModal(false)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium cursor-pointer"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="flex-1 px-4 py-3 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors font-medium cursor-pointer"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    )
}